/**
 * Competitor intelligence.
 *
 * The signal that matters here is `daysLive`. A creative still running after
 * 60 days is being paid for because it works — that's a far stronger signal
 * than engagement counts, which are gameable and often absent from ad libraries.
 * So the default sort is longevity, and we say why on screen.
 */
import { useEffect, useState } from 'react';
import { api, type CompetitorAd, type Product, type Platform } from '../api';

const PLATFORMS = ['all', 'meta', 'tiktok', 'pinterest'] as const;

export default function Competitors() {
  const [ads, setAds] = useState<CompetitorAd[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [query, setQuery] = useState('');
  const [platform, setPlatform] = useState<string>('all');
  const [minDays, setMinDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [remixing, setRemixing] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    api.products().then(setProducts).catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .competitorSearch({
        q: query || undefined,
        platform: platform === 'all' ? undefined : platform,
        minDays: minDays || undefined,
      })
      .then(setAds)
      .catch(() => setAds([]))
      .finally(() => setLoading(false));
  }, [query, platform, minDays]);

  async function remix(ad: CompetitorAd) {
    const product = products[0];
    if (!product) {
      setToast('Sync a product first — a remix needs something to advertise.');
      return;
    }
    setRemixing(ad.id);
    try {
      await api.remixCompetitor({
        competitorAdId: ad.id,
        productId: product.id,
        platforms: ['meta', 'tiktok'] as Platform[],
      });
      setToast(`Remixed into Creative Studio for ${product.title}`);
    } catch {
      setToast('Remix failed — check your connection.');
    } finally {
      setRemixing(null);
      setTimeout(() => setToast(null), 3000);
    }
  }

  return (
    <>
      <p className="eyebrow">Tier 2 · Intelligence</p>
      <h1 className="pg-title">Competitor intelligence</h1>
      <p className="pg-sub">
        Mined nightly from the Meta and TikTok ad libraries. Ads still running after two weeks are
        working — that's the ranking signal, and every one we ingest feeds your hook library.
      </p>

      <div className="filter-bar">
        <input
          className="input"
          placeholder="Search brand, copy, or industry…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {PLATFORMS.map((p) => (
          <button
            key={p}
            className={`chip ${platform === p ? 'on' : ''}`}
            onClick={() => setPlatform(p)}
          >
            {p}
          </button>
        ))}
        <button
          className={`chip ${minDays === 30 ? 'on' : ''}`}
          onClick={() => setMinDays(minDays === 30 ? 0 : 30)}
          title="Ads running 30+ days are proven performers"
        >
          30+ days live
        </button>
      </div>

      {loading ? (
        <div className="empty">Loading competitor ads…</div>
      ) : ads.length === 0 ? (
        <div className="empty">
          No competitor ads match. The nightly crawl populates this — if it's empty on a fresh
          install, that's expected until the first run.
        </div>
      ) : (
        <div className="comp-grid">
          {ads.map((ad) => (
            <article key={ad.id} className="card card-p comp-card">
              <div className="comp-head">
                <h3>{ad.headline}</h3>
                <span className={`badge b-${ad.platform}`}>{ad.platform}</span>
              </div>
              <p className="comp-brand">
                {ad.brand}
                {ad.industry ? ` · ${ad.industry}` : ''}
              </p>
              {ad.primaryText && <p className="comp-body">{ad.primaryText}</p>}
              <div className="comp-stats">
                <span title="Longer means the advertiser keeps paying for it">
                  <strong>{ad.daysLive}</strong> days live
                </span>
                {ad.engagementScore != null && (
                  <span>
                    engagement <strong>{ad.engagementScore.toFixed(2)}</strong>
                  </span>
                )}
                <button
                  className="btn sm"
                  onClick={() => void remix(ad)}
                  disabled={remixing === ad.id}
                >
                  {remixing === ad.id ? 'Remixing…' : 'Remix in my voice'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {toast && <div className="toast show">{toast}</div>}
    </>
  );
}
